import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/** The standard white surface used across every page. */
export function Card({
  children,
  className,
  padded = true,
  as: Tag = 'section',
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
  as?: 'section' | 'div' | 'article' | 'aside';
}) {
  return (
    <Tag
      className={cn(
        'rounded-xl border border-line bg-white shadow-card',
        padded && 'p-5',
        className,
      )}
    >
      {children}
    </Tag>
  );
}

export function CardHeader({
  title,
  subtitle,
  icon,
  actions,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="flex min-w-0 items-start gap-3">
        {icon && <span className="shrink-0">{icon}</span>}
        <div className="min-w-0">
          <h3 className="truncate text-card-title text-ink">{title}</h3>
          {subtitle && <p className="mt-0.5 text-helper text-muted">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Small square tinted icon holder used on KPI and compliance cards. */
export function IconTile({
  children,
  tone = 'signal',
  size = 'md',
  className,
}: {
  children: ReactNode;
  tone?: 'signal' | 'gold' | 'danger' | 'digital' | 'neutral';
  size?: 'sm' | 'md';
  className?: string;
}) {
  const tones = {
    signal: 'bg-signal-50 text-signal',
    gold: 'bg-gold-50 text-gold-600',
    danger: 'bg-danger-50 text-danger',
    digital: 'bg-digital-50 text-digital',
    neutral: 'bg-cloud text-muted',
  } as const;

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-lg',
        size === 'md' ? 'h-10 w-10 [&>svg]:h-5 [&>svg]:w-5' : 'h-8 w-8 [&>svg]:h-4 [&>svg]:w-4',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * Definition row used on the summary cards — label left, value right.
 * Labels are uppercase small caps per guideline §03.
 */
export function DetailRow({
  label,
  value,
  className,
  valueClassName,
}: {
  label: ReactNode;
  value: ReactNode;
  className?: string;
  valueClassName?: string;
}) {
  return (
    <div className={cn('flex items-start justify-between gap-6 py-[7px]', className)}>
      <dt className="shrink-0 text-2xs font-semibold uppercase tracking-[0.06em] text-muted">{label}</dt>
      <dd className={cn('min-w-0 text-right text-sm text-body', valueClassName)}>{value}</dd>
    </div>
  );
}

/** Section heading above a group of cards. */
export function SectionHeading({ children, className }: { children: ReactNode; className?: string }) {
  return <h2 className={cn('text-section-title text-ink', className)}>{children}</h2>;
}
